import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { AnalyticsTracker } from "@/components/tracking/AnalyticsTracker";
import { BehaviorTracker } from "@/components/tracking/BehaviorTracker";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { useErrorMonitoring } from "@/hooks/useErrorMonitoring";
import { ImpersonationProvider } from "@/contexts/ImpersonationContext";
import { ImpersonationBadge } from "@/components/ImpersonationBadge";

// Critical routes - load immediately
import Index from "./pages/Index";
import Auth from "./pages/Auth";

// Lazy-loaded routes for better performance
const BlogPost = lazy(() => import("./pages/BlogPost"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const ServicosPage = lazy(() => import("./pages/ServicosPage"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const PlanosPage = lazy(() => import("./pages/PlanosPage"));
const Admin = lazy(() => import("./pages/Admin"));
const AI4MEChat = lazy(() => import("./pages/AI4MEChat"));
const AI4MEControl = lazy(() => import("./pages/AI4MEControl"));
const DocumentsPage = lazy(() => import("./pages/DocumentsPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const LgpdPolicy = lazy(() => import("./pages/LgpdPolicy"));
const TestMercadoPago = lazy(() => import("./pages/TestMercadoPago"));
const TestSubscription = lazy(() => import("./pages/TestSubscription"));
const ImageAnalysisHistoryPage = lazy(() => import("./pages/ImageAnalysisHistoryPage"));
const ImageAnalysisTestPage = lazy(() => import("./pages/ImageAnalysisTestPage"));
const CouponPage = lazy(() => import("./pages/CouponPage"));

const ExcelAnalyzerPage = lazy(() => import("./pages/ExcelAnalyzerPage"));
// ProfilePage removido - funcionalidades movidas para InformacoesNegocioPage
const CreditosPage = lazy(() => import("./pages/CreditosPage"));
const CreditsAnalyticsPage = lazy(() => import("./pages/CreditsAnalyticsPage"));
const IndicacoesPage = lazy(() => import("./pages/IndicacoesPage"));
const FAQ = lazy(() => import("./pages/FAQ"));
const StatusPublico = lazy(() => import("./pages/StatusPublico"));
const BetaOnboarding = lazy(() => import("./pages/BetaOnboarding"));
const BetaFeedback = lazy(() => import("./pages/BetaFeedback"));
const BetaLandingPage = lazy(() => import("./pages/BetaLandingPage"));
const AI4MEVoiceTestPage = lazy(() => import("./pages/AI4MEVoiceTestPage"));
const TermosDeUso = lazy(() => import("./pages/TermosDeUso"));
const InstallApp = lazy(() => import("./pages/InstallApp"));
const TaxConsultingPage = lazy(() => import("./pages/TaxConsultingPage"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ConfigurarPerfilPage = lazy(() => import("./pages/ConfigurarPerfilPage"));
const LiveSphereDemo = lazy(() => import("./pages/LiveSphereDemo"));
const PagamentoSucessoPage = lazy(() => import("./pages/PagamentoSucessoPage"));
const VoiceDiagnosticsPage = lazy(() => import("./pages/VoiceDiagnosticsPage"));
const MeuPINPage = lazy(() => import("./pages/MeuPINPage"));
const MeuPINPreviewPage = lazy(() => import("./pages/MeuPINPreviewPage"));
const HistoricoConversasPage = lazy(() => import("./pages/HistoricoConversasPage"));
const InformacoesNegocioPage = lazy(() => import("./pages/InformacoesNegocioPage"));
const CursoIA = lazy(() => import("./pages/CursoIA"));
const CursoIASucesso = lazy(() => import("./pages/CursoIASucesso"));
const CursoIAAcessar = lazy(() => import("./pages/CursoIAAcessar"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const queryClient = new QueryClient();

const AppContent = () => {
  useErrorMonitoring(); // v3
  return (
    <BrowserRouter>
      <Toaster />
      <Sonner />
      <ImpersonationBadge />
      <AnalyticsTracker />
      <BehaviorTracker />
      <Suspense fallback={<PageLoader />}>
        <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/login" element={<Navigate to="/auth" replace />} />
            <Route path="/painel" element={<Dashboard />} />
            <Route path="/dashboard" element={<Navigate to="/painel" replace />} />
            <Route path="/planos" element={<PlanosPage />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/prompts" element={<ServicosPage />} />
            <Route path="/ai4me" element={<AI4MEChat />} />
            <Route path="/documentos" element={<DocumentsPage />} />
            <Route path="/documents" element={<DocumentsPage />} />
            <Route path="/ai4me-controle" element={<AI4MEControl />} />
            <Route path="/artigo/:id" element={<BlogPost />} />
            <Route path="/lgpd" element={<LgpdPolicy />} />
            <Route path="/cupom" element={<CouponPage />} />
            <Route path="/test-mercadopago" element={<TestMercadoPago />} />
            <Route path="/test-subscription" element={<TestSubscription />} />
            
            <Route path="/analisar-excel" element={<ExcelAnalyzerPage />} />
            <Route path="/perfil" element={<Navigate to="/informacoes-negocio" replace />} />
            <Route path="/image-history" element={<ImageAnalysisHistoryPage />} />
            <Route path="/image-test" element={<ImageAnalysisTestPage />} />
            <Route path="/creditos" element={<CreditosPage />} />
            <Route path="/creditos/analytics" element={<CreditsAnalyticsPage />} />
            <Route path="/indicacao" element={<IndicacoesPage />} />
            <Route path="/indicacoes" element={<IndicacoesPage />} />
            <Route path="/ajuda" element={<FAQ />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/status" element={<StatusPublico />} />
            <Route path="/beta" element={<BetaLandingPage />} />
            <Route path="/beta-onboarding" element={<BetaOnboarding />} />
            <Route path="/beta-feedback" element={<BetaFeedback />} />
            <Route path="/ai4me-voice-test" element={<AI4MEVoiceTestPage />} />
            <Route path="/termos-de-uso" element={<TermosDeUso />} />
            <Route path="/termos" element={<TermosDeUso />} />
            <Route path="/instalar" element={<InstallApp />} />
            <Route path="/install" element={<InstallApp />} />
            <Route path="/consultoria-tributaria" element={<TaxConsultingPage />} />
            <Route path="/redefinir-senha" element={<ResetPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/painel/configurar-perfil" element={<ConfigurarPerfilPage />} />
            <Route path="/configurar-perfil" element={<ConfigurarPerfilPage />} />
            <Route path="/live-sphere" element={<LiveSphereDemo />} />
            <Route path="/pagamento-sucesso" element={<PagamentoSucessoPage />} />
            <Route path="/admin/voice-diagnostics" element={<VoiceDiagnosticsPage />} />
            <Route path="/meu-pin" element={<MeuPINPage />} />
            <Route path="/meu-pin/preview" element={<MeuPINPreviewPage />} />
            <Route path="/historico-conversas" element={<HistoricoConversasPage />} />
            <Route path="/informacoes-negocio" element={<InformacoesNegocioPage />} />
            <Route path="/anamnese" element={<InformacoesNegocioPage />} />
            <Route path="/curso-ia" element={<CursoIA />} />
            <Route path="/curso-ia/sucesso" element={<CursoIASucesso />} />
            <Route path="/curso-ia/acessar" element={<CursoIAAcessar />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <ImpersonationProvider>
        <TooltipProvider>
          <AppContent />
        </TooltipProvider>
      </ImpersonationProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
